import React, {
    useEffect,
    useState,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import sportsApi from '../utils/sportsApi';

import ModalBody from '../components/common/modals/ModalBody';
import ModalHeader from '../components/common/modals/ModalHeader';
import ModalFooter from '../components/common/modals/ModalFooter';
import Carrousel from '../components/modal/Carrousel';

import classNames from '@app/ui/sports-components/utils/classNames';
import { Button, Row, Col, Flex } from '@app/ui';

import BoxDrawer from '../components/boxes/BoxDrawer';
import updatePlacementBoundingBox from '../actions/settings/updatePlacementBoundingBox';
import sendNotification from '../actions/notifications/send';
import combineActions from '../utils/combineActions';
import {
    BBClientToDb,
    BBDbToClient,
    addTempId,
    getTempId,
} from '../utils/boundingBoxes';
import { Context } from './contexts/OnboardingContext';
import { PlacementContext } from './contexts/PlacementContext';

const domNode = document.getElementById('placement-bounding-box-modal');

const BoundingBoxModal = ({
    updatePlacementBoundingBox,
    client,
    sendNotification,
}) => {
    const { t } = useTranslation('settings');
    const [examples, setExamples] = useState([]);
    const [tempBoxes, setTempBoxes] = useState([]);
    const [clientImage, setClientImage] = useState({});

    const {
        files,
        setFiles,
        showModal,
        setShowModal,
        placementExampleId,
        setPlacementExampleId,
        showReloImages,
        reloImages,
        reloImageId,
        setReloImageId,
        setShowReloImages,
    } = useContext(PlacementContext);

    const {
        rhId,
        structureLocked,
        currentSelectedPlacement: placement,
    } = useContext(Context);

    const canEdit = !showReloImages && !structureLocked;

    const close = () => {
        setShowModal(false);
        if (showReloImages) {
            setShowReloImages(false);
        }
    };

    const placementExample = examples.find(e => e.id === placementExampleId);
    const reloExample = reloImages.find(e => e.id === reloImageId);
    const imageUrl = useMemo(() => {
        if (!showReloImages)
            return placementExample && placementExample.imageUrl
                ? placementExample.imageUrl
                : null;
        return reloExample && reloExample.imageUrl
            ? reloExample.imageUrl
            : null;
    }, [showReloImages, placementExample, reloImages, reloExample]);

    const initBB = useCallback(
        ci => {
            setClientImage(ci);
            if (showReloImages) return;
            const boxes =
                placementExample && placementExample.boundingBoxes
                    ? placementExample.boundingBoxes.map(b => {
                          return {
                              ...b,
                              tempId: getTempId(BBDbToClient(ci)(b)),
                          };
                      })
                    : [];
            setTempBoxes(boxes);
        },
        [placementExample, showReloImages],
    );

    const showBoxes = clientImage
        ? tempBoxes.map(BBDbToClient(clientImage))
        : [];

    useEffect(() => {
        let exampleList = [];
        if (placement) {
            if (placement.examples) {
                const exs = placement.examples.map(ex => {
                    return { ...ex, isNew: false };
                });
                exampleList = [...exampleList, ...exs];
            }
        }
        if (files) {
            const fls = files.map(f => {
                return { ...f, imageUrl: f.url, isNew: true };
            });
            exampleList = [...exampleList, ...fls];
        }
        setExamples(exampleList);
    }, [placement, files]);

    const currentExamples = examples.reduce((acc, ex) => {
        if (ex.id === placementExampleId) {
            acc.push({
                ...ex,
                boundingBoxes: tempBoxes,
            });
        } else {
            acc.push(ex);
        }
        return acc;
    }, []);

    const modifyDB = useCallback(
        action =>
            async ({ tempId, box }) => {
                const { isNew } = placementExample;
                if (!box && !tempId) return;

                const dbBox = box
                    ? BBClientToDb(clientImage)(addTempId(box))
                    : null;
                const couldNotSave =
                    (action === 'create' || action === 'update') &&
                    (!dbBox || dbBox.width <= 0 || dbBox.height <= 0);

                if (couldNotSave) {
                    sendNotification(
                        t('clientConfiguration.boundingBox.errorNotDrawn'),
                        {
                            context: 'warning',
                        },
                    );
                }
                let newBoxes = [];

                const oldBox = tempId
                    ? tempBoxes.find(ob => ob.tempId === tempId)
                    : {};

                const currentBB =
                    (placementExample && placementExample.boundingBoxes) || [];
                let bxs = [];
                let bx = {};
                switch (action) {
                    case 'create':
                        newBoxes = [...tempBoxes, ...(dbBox ? [dbBox] : [])];
                        break;
                    case 'update':
                        newBoxes = [
                            ...tempBoxes.filter(tb => tb.tempId !== tempId),
                            ...(dbBox ? [dbBox] : []),
                        ];
                        break;
                    case 'remove':
                        newBoxes = tempBoxes.filter(tb => tb.tempId !== tempId);
                        break;
                }
                setTempBoxes(newBoxes);
                if (!isNew) {
                    switch (action) {
                        case 'create':
                            bx = await addBB(dbBox, couldNotSave);
                            bxs = [...currentBB, ...(bx ? [bx] : [])];
                            if (!couldNotSave) {
                                newBoxes[newBoxes.length - 1] = {
                                    ...newBoxes[newBoxes.length - 1],
                                    id: bx.id,
                                };
                                setTempBoxes(newBoxes);
                            }
                            break;
                        case 'update':
                            [bx] = await Promise.all([
                                addBB(dbBox, couldNotSave),
                                deleteBB(oldBox),
                            ]);
                            bxs = [
                                ...currentBB.filter(b => oldBox.id !== b.id),
                                ...(bx ? [bx] : []),
                            ];
                            if (!couldNotSave) {
                                newBoxes[newBoxes.length - 1] = {
                                    ...newBoxes[newBoxes.length - 1],
                                    id: bx.id,
                                };
                                setTempBoxes(newBoxes);
                            }
                            break;
                        case 'remove':
                            await deleteBB(oldBox);
                            bxs = currentBB.filter(b => oldBox.id !== b.id);
                            break;
                    }
                } else {
                    bxs = newBoxes
                        .filter(({ width, height }) => width > 0 && height > 0)
                        .map(({ topLeftX, topLeftY, width, height }) => {
                            return {
                                topLeftX,
                                topLeftY,
                                width,
                                height,
                            };
                        });

                    const newFiles = files.reduce((acc, f) => {
                        if (f.id === placementExampleId) {
                            acc.push({ ...f, boundingBoxes: bxs });
                        } else {
                            acc.push(f);
                        }
                        return acc;
                    }, []);
                    setFiles(newFiles);
                }

                if (placement) {
                    await updatePlacementBoundingBox({
                        rhId,
                        plId: placement.id,
                        exId: placementExampleId,
                        boxes: bxs,
                    });
                }
            },
        [
            tempBoxes,
            placementExample,
            files,
            placementExampleId,
            clientImage,
            placement,
        ],
    );

    const deleteBB = async box => {
        try {
            if (!box || !box.id) return;
            const endPoint = `/clients/${client.id}/rightsholders/${rhId}/placements/${placement.id}/examples/${placementExampleId}/bounding-boxes`;
            await sportsApi
                .url(endPoint)
                .query({
                    ids: [box.id],
                })
                .delete()
                .res();
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const addBB = async (box, couldNotSave) => {
        try {
            if (couldNotSave) return null;
            const endPoint = `/clients/${client.id}/rightsholders/${rhId}/placements/${placement.id}/examples/${placementExampleId}/bounding-boxes`;
            const domainObject = [box];

            const payload = {
                action: 'createMultiple',
                domainObject,
            };

            const [bx] =
                (await sportsApi.url(endPoint).post(payload).json()) || [];

            return bx;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const _handleNavigation = e => {
        if (e.keyCode === 13 || e.keyCode === 27) {
            close();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', _handleNavigation);
        return () => {
            document.removeEventListener('keydown', _handleNavigation);
        };
    }, []);

    const changePlacementImage = async id => {
        try {
            if (id === placementExampleId) return;
            setPlacementExampleId(id);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    };

    const changeReloImage = async id => {
        try {
            if (id === reloImageId) return;
            setReloImageId(id);
        } catch (e) {
            // eslint-disable-next-line no-console
            sendNotification(
                t('clientConfiguration.boundingBox.errorDisplayImage'),
                {
                    context: 'warning',
                },
            );
        }
    };

    const images = showReloImages ? reloImages : currentExamples;
    const currentId = showReloImages ? reloImageId : placementExampleId;
    const change = showReloImages ? changeReloImage : changePlacementImage;

    const classes = classNames('gds-modal__overlay', {
        'gds-modal--shown': showModal,
    });

    const title = showReloImages
        ? t('clientConfiguration.boundingBox.reloTitle')
        : t('clientConfiguration.boundingBox.title');

    return ReactDOM.createPortal(
        <>
            {showModal && (
                <div className={classes} style={{ zIndex: 1100 }}>
                    <div className="gds-modal gds-layout__column--md-12">
                        <div className="gds-modal__form">
                            <ModalHeader title={title} onClose={close} />
                            <ModalBody className="-p-a-0">
                                <div className="-color-bg-dk-2">
                                    <BoxDrawer
                                        initBB={initBB}
                                        setClientImage={setClientImage}
                                        imageUrl={imageUrl}
                                        boxes={showBoxes}
                                        create={modifyDB('create')}
                                        update={modifyDB('update')}
                                        remove={modifyDB('remove')}
                                        allowBoundingBox={canEdit}
                                        disableEdit={!canEdit}
                                        hideBoxes={showReloImages}
                                    />
                                </div>
                            </ModalBody>
                            <div className="-color-bg-dk-2 bounding-box-body">
                                <Carrousel
                                    images={images}
                                    currentId={currentId}
                                    change={change}
                                    hideWarning={showReloImages}
                                />
                            </div>
                            <ModalFooter className="-p-v-3 -p-h-3">
                                <Flex
                                    vertical
                                    className="gds-layout__container"
                                >
                                    <Row gutter={16}>
                                        <Col md={20}>
                                            {!showReloImages && (
                                                <p>
                                                    <i className="far fa-fw fa-vector-square -m-r-1" />
                                                    {t(
                                                        'clientConfiguration.boundingBox.instructions',
                                                    )}
                                                </p>
                                            )}
                                        </Col>
                                        <Col md={4}>
                                            <Button
                                                color="green"
                                                size="small"
                                                className="-float-right -w-100p"
                                                variant="solid"
                                                onClick={close}
                                            >
                                                {t(
                                                    'clientConfiguration.boundingBox.done',
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Flex>
                            </ModalFooter>
                        </div>
                    </div>
                </div>
            )}
        </>,
        domNode,
    );
};

BoundingBoxModal.propTypes = {
    showModal: PropTypes.bool,
    placement: PropTypes.object,
    close: PropTypes.func,
    settings: PropTypes.object,
    placementExampleId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    rhId: PropTypes.number,
    updatePlacementBoundingBox: PropTypes.func,
    sendNotification: PropTypes.func,
    setPlacementExampleId: PropTypes.func,
    files: PropTypes.array,
    setFiles: PropTypes.func,
    client: PropTypes.object,
    canEdit: PropTypes.bool,
    showReloImages: PropTypes.bool,
};

export default connect(
    ['settings', 'client'],
    combineActions({ updatePlacementBoundingBox, sendNotification }),
)(BoundingBoxModal);
